import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Heart, Send, User, MessageSquare, CheckCircle2 } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface ContactUsProps {
  onSubmit?: (data: ContactFormData) => void;
  onCommunityForum?: () => void;
}

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const subjectOptions = [
  { value: 'technical', label: 'Technical Issue', color: 'from-red-500 to-orange-500' },
  { value: 'billing', label: 'Billing', color: 'from-green-500 to-teal-500' },
  { value: 'ai-feedback', label: 'AI Feedback', color: 'from-purple-500 to-indigo-500' },
  { value: 'general', label: 'General Inquiry', color: 'from-blue-500 to-cyan-500' }
];

export const ContactUs: React.FC<ContactUsProps> = ({ onSubmit, onCommunityForum }) => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const prefersReducedMotion = useReducedMotion();

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) return;

    setIsSubmitting(true);
    
    // Simulate API call - would integrate with Supabase
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitted(true);
    setIsSubmitting(false);
    
    // Trigger confetti
    if (!prefersReducedMotion) {
      console.log('Confetti burst on successful submission!');
    }
    
    onSubmit?.(formData);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 3000);
  };

  const selectedSubject = subjectOptions.find(option => option.value === formData.subject);

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-100 via-blue-50 to-teal-100 dark:from-green-900/20 dark:via-blue-900/20 dark:to-teal-900/20 flex items-center justify-center pt-20">
        <motion.div
          className="text-center max-w-md mx-auto px-4"
          initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-green-500 to-teal-500 rounded-full mb-6"
            animate={prefersReducedMotion ? {} : { rotate: 360 }}
            transition={{ duration: 1 }}
          >
            <CheckCircle2 className="w-12 h-12 text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Message Sent!
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Thanks for reaching out. We'll get back to you within 24 hours.
          </p>
          <motion.button
            onClick={onCommunityForum}
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-full font-medium flex items-center gap-2 mx-auto"
            whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
          >
            <Heart className="w-4 h-4" />
            Join Community Forum
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-teal-100 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-teal-900/20 pt-20">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0 opacity-30"
          animate={prefersReducedMotion ? {} : {
            background: [
              "radial-gradient(circle at 20% 80%, #8B5CF6 0%, transparent 50%)",
              "radial-gradient(circle at 80% 20%, #06B6D4 0%, transparent 50%)",
              "radial-gradient(circle at 40% 40%, #10B981 0%, transparent 50%)",
            ]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full mb-6">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            How can we help?
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            We're here to support your relationship journey. Send us a message and we'll get back to you soon.
          </p>
        </motion.div>

        {/* Contact Form */}
        <motion.div
          className="p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <motion.div
              animate={prefersReducedMotion ? {} : {
                scale: focusedField === 'name' ? 1.02 : 1
              }}
              transition={{ duration: 0.2 }}
            >
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Name
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                  className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500/30 transition-all duration-200"
                  placeholder="Your name"
                  required
                />
              </div>
            </motion.div>

            {/* Email Field */}
            <motion.div
              animate={prefersReducedMotion ? {} : {
                scale: focusedField === 'email' ? 1.02 : 1
              }}
              transition={{ duration: 0.2 }}
            >
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500/30 transition-all duration-200"
                  placeholder="your.email@example.com"
                  required
                />
              </div>
            </motion.div>

            {/* Subject Field */}
            <motion.div
              animate={prefersReducedMotion ? {} : {
                scale: focusedField === 'subject' ? 1.02 : 1
              }}
              transition={{ duration: 0.2 }}
            >
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Subject
              </label>
              <select
                id="subject"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                onFocus={() => setFocusedField('subject')}
                onBlur={() => setFocusedField(null)}
                className="w-full px-4 py-4 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500/30 transition-all duration-200"
                required
              >
                <option value="">Select a subject</option>
                {subjectOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </motion.div>

            {/* Message Field */}
            <motion.div
              animate={prefersReducedMotion ? {} : {
                scale: focusedField === 'message' ? 1.02 : 1
              }}
              transition={{ duration: 0.2 }}
            >
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Message
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField(null)}
                  rows={6}
                  className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500/30 transition-all duration-200 resize-none"
                  placeholder="Tell us how we can help you..."
                  required
                />
              </div>
            </motion.div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                type="submit"
                disabled={isSubmitting || !formData.name || !formData.email || !formData.subject || !formData.message}
                className="flex-1 px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-semibold text-lg flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={prefersReducedMotion ? {} : { 
                  scale: isSubmitting ? 1 : 1.05, 
                  boxShadow: "0 20px 40px rgba(255, 107, 53, 0.3)" 
                }}
                whileTap={prefersReducedMotion ? {} : { scale: isSubmitting ? 1 : 0.95 }}
              >
                {isSubmitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    >
                      <Send className="w-5 h-5" />
                    </motion.div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </motion.button>

              <motion.button
                type="button"
                onClick={onCommunityForum}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-full font-medium flex items-center justify-center gap-2"
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
              >
                <Heart className="w-5 h-5" />
                Community Forum
              </motion.button>
            </div>
          </form>
        </motion.div>

        {/* Additional Help */}
        <motion.div
          className="mt-12 grid md:grid-cols-2 gap-6"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="p-6 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Quick Response
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              We typically respond within 24 hours during business days.
            </p>
          </div>
          
          <div className="p-6 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Emergency Support
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              For urgent issues, reach us at{' '}
              <a href="mailto:urgent@octify.app" className="text-purple-500 hover:text-purple-400">
                urgent@octify.app
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};